import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Nav from './Nav';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import { decomposeImportedPage } from './importFlow';
import EdgeSentence from './EdgeSentence';
import FeedManager from './FeedManager';
import TagField from './TagField';
import AccessControl, { accessToFlags, describeAccess } from './AccessControl';
import { Explain } from './Explain';

// The Writer (DECISIONS.md 2026-07-22 "The Writer" + same-day rulings entry):
// the Reader's write-side twin. Entries are sealed-by-default text objects,
// asserted_at = start of writing, minted on first non-trivial content. Modes
// are tags (born inert — NO creator auto-vote; mode never implies visibility).
// Snapshots: the living entry keeps its id; a frozen 'snapshot' copy is linked
// by `revises` (star topology, nothing rewires). No generative AI in the
// composition path, by decision.
//
// ONE DOOR since 2026-08-07 (DECISIONS.md "Write and Add merge"): /dump's Add
// surface folded in here, and the ingestion/composition split moved from page
// level into the behavior of THE BOX — the same below-page-level move as
// Home + Viewsets. What that means concretely:
//
// - Anything that is not a lone URL is a living autosaved entry, exactly as
//   before.
// - A lone URL (pre-mint) PAUSES autosave — without the pause the 1.2s timer
//   would mint a stray text object containing the URL — and offers the page
//   import instead: fetch + archive fallback + source attribution, claims
//   extracted when a model is allowed to read it, opens in the Reader.
// - The model-visible checkbox became the three-tier control, and on 2026-08-08
//   became TWO AXES (AccessControl.js): who can read it (anyone/a group/only me)
//   and which models may (any/local only/none). Private + no model stays the
//   default; still set at mint, locked after, and it survives `done · new entry`.
// - AI suggestions are OPT-IN AFTER `done` (owner 2026-08-07), never during
//   composition and never at the sealed tier — Add auto-ran them on save;
//   the Writer's writing-first ruling wins on the merged surface.
// - The clipper handoff (?url=, ?clip=&clip_source=) pre-fills this box;
//   /dump redirects here with its query string intact, so the extension
//   needs no change.
// - Feed subscribe + the structured-create pointer live behind folds at the
//   bottom, keeping the one-door promise of 2026-07-07.

const SUGGESTED_TAGS = ['journal', 'creative', 'study', 'research', 'social-draft'];
const LINK_RELATIONS = ['works_toward', 'reflects_on'];
const AUTOSAVE_MS = 1200;
const TRAIL_MAX = 6;

// THE THREE TIERS BECAME TWO AXES on 2026-08-08 (DECISIONS.md "Two visibility
// axes"). The mapping and the control both moved to AccessControl.js, which is
// now the only client-side mirror of backend/lib/access.ts — the `tierFlags`
// table that used to live here and the second copy in
// scripts/transcribe_upload.js were both deleted with it.
//
// Note what the old table could not say. Its `private_ai` set `ai_visible: true`,
// i.e. readable by ANY model including a third party; the name was about the
// human axis and silent about the model one. And no tier could express `group`
// at all. Both are ordinary choices now.
const DEFAULT_ACCESS = { human: 'private', model: 'sealed' };

// A lone URL imports as a page (fetch + extract + source attribution)
// instead of minting as a text entry — the graph grows as a side effect
// of reading.
const URL_ONLY = /^https?:\/\/\S+$/i;

const jsonPost = (path, body) =>
  apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export default function Write() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entry, setEntry] = useState(null); // the living object, once minted
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('blank'); // blank | unsaved | saving | saved | error
  // Set at mint, locked after; survives done·new. Two axes since 2026-08-08.
  const [access, setAccess] = useState(DEFAULT_ACCESS);
  const [entryTierLabel, setEntryTierLabel] = useState(null); // what the current entry was minted at
  const [stagedTags, setStagedTags] = useState([]); // tag names picked before the entry exists
  const [trail, setTrail] = useState([]); // entries finished in this sitting
  const [edges, setEdges] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [showConnections, setShowConnections] = useState(false);
  const [linkRelation, setLinkRelation] = useState('works_toward');
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [note, setNote] = useState(null); // transient import/suggestion messages
  const [clipSource, setClipSource] = useState(null); // page of origin for a ?clip= handoff
  const [suggest, setSuggest] = useState(null); // { id, label, phase, tags, relations, contextById }
  const [feedsOpen, setFeedsOpen] = useState(false);

  const startedAtRef = useRef(null); // asserted_at: first keystroke of this entry
  const saveTimerRef = useRef(null);
  const mintingRef = useRef(false);
  const textareaRef = useRef(null);
  const contentRef = useRef('');
  contentRef.current = content;
  // Staged tags ride a ref as well as state: an autosave scheduled BEFORE a tag
  // was staged would otherwise mint the entry with the tag list it captured at
  // keystroke time, and the tag would be silently dropped.
  const stagedTagsRef = useRef([]);
  stagedTagsRef.current = stagedTags;
  // Same reasoning for the clip source: the autosave that mints must see it.
  const clipSourceRef = useRef(null);
  clipSourceRef.current = clipSource;
  const accessRef = useRef(DEFAULT_ACCESS);
  accessRef.current = access;

  // Import mode: a lone URL before anything is minted. Once an entry exists,
  // a URL is just the entry's content — autosave carries on.
  const urlMode = !entry && URL_ONLY.test(content.trim());

  useEffect(() => {
    apiFetch('/api/relations')
      .then((r) => r.json())
      .then(setRelationTypes)
      .catch(() => {});
  }, []);

  // Reopen an existing entry via /write?id=; clipper/share handoffs pre-fill
  // via ?url= (page clip) or ?clip=&clip_source= (selection + its page of
  // origin). Handoffs only pre-fill — the privacy tier stays a human choice,
  // which is why a clip is NOT autosaved on arrival: it mints on your first
  // edit, Ctrl+S, or done, after the tier is what you want.
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && !entry) {
      apiFetch(`/api/objects/${id}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((obj) => {
          if (obj && obj.object_type === 'text') {
            setEntry(obj);
            setContent(obj.content);
            setEntryTierLabel(obj.model_access || (obj.ai_visible ? 'public_ai' : 'sealed'));
            setStatus('saved');
          }
        })
        .catch(() => {});
      return;
    }
    const url = searchParams.get('url');
    const clip = searchParams.get('clip');
    if (url) setContent(url);
    else if (clip) {
      setContent(clip);
      setClipSource(searchParams.get('clip_source') || null);
      startedAtRef.current = new Date().toISOString();
      setStatus('unsaved');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refreshEdges = useCallback((id) => {
    apiFetch(`/api/relation-instances/${id}`)
      .then((r) => r.json())
      .then((rows) => setEdges(rows.filter((e) => !e.is_system)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (entry) refreshEdges(entry.id);
  }, [entry, refreshEdges]);

  // ---- relation-type + tag plumbing (free namespace, get-or-create) ----

  const ensureRelationType = async (name) => {
    const found = relationTypes.find((r) => r.name === name);
    if (found) return found.id;
    const res = await jsonPost('/api/relations', { name });
    if (res.ok) {
      const created = await res.json();
      setRelationTypes((prev) => [...prev, created]);
      return created.id;
    }
    // lost a create race — refetch and look again
    const all = await (await apiFetch('/api/relations')).json();
    setRelationTypes(all);
    return all.find((r) => r.name === name)?.id;
  };

  // One round trip, get-or-create on both halves (POST /api/tags). It replaced
  // a client-side search-then-create that could mint a duplicate tag, and that
  // minted tags SEALED while every other tagging path in the app made them
  // model-visible.
  const attachTag = useCallback(async (entryId, name) => {
    const res = await jsonPost('/api/tags', { name, object_id: entryId });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Could not attach that tag');
    return json;
  }, []);

  // Selection clip: also import the page it came from (dedupes if already in
  // the graph) and record provenance — the quote arrives as "derived_from →
  // page", source-attributed from birth. Non-fatal: the entry saved fine.
  const linkClipSource = useCallback(async (entryId, sourceUrl) => {
    try {
      const sres = await jsonPost('/api/import/url', { url: sourceUrl, ...accessRef.current });
      const sdata = await sres.json();
      if (sres.ok && sdata.page) {
        const relId = await ensureRelationType('derived_from');
        if (relId) {
          await jsonPost('/api/relation-instances', {
            relation_id: relId,
            source_id: entryId,
            target_id: sdata.page.id,
            polarity: 1,
          });
        }
        setNote(`Clip linked to its source page${sdata.fetched_from === 'archive' ? ' (via Internet Archive snapshot)' : ''}.`);
      } else {
        setNote(`Clip saved; its source page could not be imported (${sdata.error || 'fetch failed'}).`);
      }
    } catch {
      setNote('Clip saved; linking its source page failed.');
    }
    setClipSource(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationTypes]);

  // ---- autosave: mint on first non-trivial content, PUT after ----

  // Returns the live entry object, so a caller that flushes a save (finish, or
  // snapshot) can act on an entry this very call minted — `entry` state is not
  // yet updated inside the same closure.
  const save = useCallback(async () => {
    const text = contentRef.current;
    if (!text.trim()) return entry;
    if (!entry && URL_ONLY.test(text.trim())) return null; // import mode — nothing mints
    setStatus('saving');
    try {
      if (!entry) {
        if (mintingRef.current) return null;
        mintingRef.current = true;
        const flags = accessToFlags(accessRef.current);
        const res = await jsonPost('/api/objects', {
          content: text,
          object_type: 'text',
          visibility_targets: flags.visibility_targets,
          model_access: flags.model_access,
          asserted_at: startedAtRef.current,
        });
        const obj = await res.json();
        mintingRef.current = false;
        if (!res.ok) throw new Error(obj.error || 'create failed');
        setEntry(obj);
        setEntryTierLabel(describeAccess(accessRef.current));
        setSearchParams({ id: obj.id }, { replace: true });
        for (const name of stagedTagsRef.current) await attachTag(obj.id, name);
        setStagedTags([]);
        if (clipSourceRef.current) await linkClipSource(obj.id, clipSourceRef.current);
        refreshEdges(obj.id);
        setStatus('saved');
        return obj;
      }
      const res = await apiFetch(`/api/objects/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text }),
      });
      if (!res.ok) throw new Error('save failed');
      setStatus('saved');
      return entry;
    } catch (err) {
      mintingRef.current = false;
      console.error('Writer save error:', err);
      setStatus('error');
      return entry;
    }
  }, [entry, attachTag, linkClipSource, refreshEdges, setSearchParams]);

  const handleChange = (e) => {
    const text = e.target.value;
    if (!startedAtRef.current && text.trim()) startedAtRef.current = new Date().toISOString();
    setContent(text);
    clearTimeout(saveTimerRef.current);
    if (!entry && URL_ONLY.test(text.trim())) {
      // Import mode: pause autosave so no stray text object mints while the
      // URL sits in the box. The import panel takes over.
      setStatus('blank');
      return;
    }
    setStatus(text.trim() ? 'unsaved' : 'blank');
    if (text.trim()) saveTimerRef.current = setTimeout(save, AUTOSAVE_MS);
  };

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      clearTimeout(saveTimerRef.current);
      save();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (urlMode) importPage();
      else finishAndStartNew();
    }
  };

  // ---- the URL import path (Add's page pipeline, unchanged semantics) ----

  const importPage = async () => {
    const url = contentRef.current.trim();
    if (!URL_ONLY.test(url) || importBusy) return;
    setImportBusy(true);
    setNote(null);
    try {
      const res = await jsonPost('/api/import/url', { url, ...access });
      const data = await res.json();
      if (!res.ok) {
        setNote(data.error || 'Could not import that page.');
        return;
      }
      if (data.existing) {
        navigate(`/object/${(data.page || data.link).id}`);
        return;
      }
      // Pages open in the Reader. At a model-visible tier the claims are
      // extracted on the way, so the article arrives underlined; sealed
      // pages land there too — the Reader explains why nothing is marked.
      if (access.model !== 'sealed') {
        setNote('Imported — extracting claims…');
        await decomposeImportedPage(data.page, access);
      }
      navigate(`/read/${data.page.id}?d=1`);
    } catch (err) {
      console.error('Writer import error:', err);
      setNote('Something went wrong importing. Please try again.');
    } finally {
      setImportBusy(false);
    }
  };

  // ---- tags ----
  //
  // Two states, one field: before the entry exists the tags are STAGED (applied
  // in the same pass that mints it), after it exists they are `tagged` edges.

  const tags = entry
    ? edges
        .filter((e) => e.relation_name === 'tagged' && e.source_id === entry.id)
        .map((e) => ({ key: e.id, name: e.target_content, edgeId: e.id }))
    : stagedTags.map((name) => ({ key: name, name }));

  const addTag = async (name) => {
    if (!entry) {
      setStagedTags((prev) =>
        prev.some((t) => t.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]
      );
      return;
    }
    await attachTag(entry.id, name);
    refreshEdges(entry.id);
  };

  const removeTag = async (tag) => {
    if (!tag.edgeId) {
      setStagedTags((prev) => prev.filter((t) => t !== tag.name));
      return;
    }
    await apiFetch(`/api/relation-instances/${tag.edgeId}`, { method: 'DELETE' });
    refreshEdges(entry.id);
  };

  // ---- finish this entry, start a blank one ----
  //
  // The Writer's second job (and the reason the Reader's twin is also the quick
  // note door): several short, unrelated things in one sitting. Finishing flushes
  // the pending autosave, drops the finished entry into the session trail so it
  // stays one click away, and resets everything an entry owns — content,
  // asserted_at, tags, edges — while keeping the tier choice, which is a
  // property of how you are working rather than of one note.

  // Reopening from the trail is the same flush-then-swap as finishing, minus
  // the blank: whatever is in the box is saved before the swap, never dropped.
  const openEntry = async (id) => {
    if (entry?.id === id) return;
    clearTimeout(saveTimerRef.current);
    if (contentRef.current.trim()) await save();
    const res = await apiFetch(`/api/objects/${id}`);
    if (!res.ok) return;
    const obj = await res.json();
    setEntry(obj);
    setContent(obj.content);
    contentRef.current = obj.content;
    setEntryTierLabel(obj.model_access || 'sealed');
    setStatus('saved');
    setStagedTags([]);
    setEdges([]);
    startedAtRef.current = null;
    setSearchParams({ id }, { replace: true });
    textareaRef.current?.focus();
  };

  const finishAndStartNew = async () => {
    clearTimeout(saveTimerRef.current);
    const finished = contentRef.current.trim() ? await save() : entry;
    if (finished) {
      const text = contentRef.current.trim() || finished.content || '';
      const label = text.length > 48 ? `${text.slice(0, 48)}…` : text || 'untitled';
      setTrail((prev) =>
        [{ id: finished.id, label }, ...prev.filter((t) => t.id !== finished.id)].slice(0, TRAIL_MAX)
      );
      // Writing first, AI after (owner 2026-08-07): at a model-visible tier the
      // filed entry earns an OFFER of suggestions — one explicit tap away,
      // never auto-run, never mid-composition, never for sealed content.
      if (access.model !== 'sealed' && (finished.model_access ?? 'public_ai') !== 'sealed') {
        setSuggest({ id: finished.id, label, phase: 'offer' });
      }
    }
    setEntry(null);
    setContent('');
    contentRef.current = '';
    setStatus('blank');
    setEntryTierLabel(null);
    setStagedTags([]);
    setEdges([]);
    setShowConnections(false);
    startedAtRef.current = null;
    setSearchParams({}, { replace: true });
    textareaRef.current?.focus();
  };

  // ---- post-done AI suggestions (Add's review, made opt-in) ----

  const fetchSuggestions = async () => {
    if (!suggest) return;
    setSuggest((s) => ({ ...s, phase: 'loading' }));
    try {
      const res = await apiFetch(`/api/objects/${suggest.id}/suggest`, { method: 'POST' });
      if (res.status === 503) {
        setNote('AI suggestions are unavailable (no API key configured).');
        setSuggest(null);
        return;
      }
      if (!res.ok) throw new Error('suggest failed');
      const data = await res.json();
      const contextById = {};
      for (const c of data.context || []) contextById[c.id] = c;
      setSuggest((s) => ({
        ...s,
        phase: 'review',
        contextById,
        tags: (data.suggestions.tags || []).map((name) => ({ name, on: true })),
        relations: (data.suggestions.relations || []).map((r) => ({ ...r, on: true })),
      }));
    } catch (err) {
      console.error('Writer suggest error:', err);
      setNote('Fetching suggestions failed.');
      setSuggest(null);
    }
  };

  const applySuggestions = async () => {
    try {
      const res = await jsonPost(`/api/objects/${suggest.id}/apply`, {
        tags: suggest.tags.filter((t) => t.on).map((t) => t.name),
        relations: suggest.relations
          .filter((r) => r.on)
          .map(({ target_id, relation_name, polarity }) => ({ target_id, relation_name, polarity })),
      });
      if (!res.ok) throw new Error('apply failed');
      setNote('Suggestions saved — born inert; your votes activate them.');
    } catch (err) {
      console.error('Writer apply error:', err);
      setNote('Failed to save the selected suggestions.');
    }
    setSuggest(null);
  };

  // ---- goal / reflection links ----

  const searchLinkTargets = async () => {
    if (!linkQuery.trim()) return;
    const res = await apiFetch(`/api/objects/search?q=${encodeURIComponent(linkQuery.trim())}`);
    const rows = await res.json();
    setLinkResults(rows.filter((o) => o.id !== entry?.id).slice(0, 8));
  };

  const addLink = async (targetId) => {
    const relId = await ensureRelationType(linkRelation);
    await jsonPost('/api/relation-instances', {
      relation_id: relId,
      source_id: entry.id,
      target_id: targetId,
      polarity: 1,
    });
    setLinkQuery('');
    setLinkResults(null);
    refreshEdges(entry.id);
  };

  // A goal that doesn't exist yet is a claim about intent — sealed + private
  // like the entry; it earns visibility the same explicit way anything does.
  const createAndLink = async () => {
    const res = await jsonPost('/api/objects', {
      content: linkQuery.trim(),
      object_type: 'text',
      visibility_targets: [],
      model_access: 'sealed',
    });
    const goal = await res.json();
    await addLink(goal.id);
  };

  // ---- snapshot: frozen copy, living entry keeps its id ----

  const takeSnapshot = async () => {
    if (!entry || snapshotting) return;
    setSnapshotting(true);
    try {
      clearTimeout(saveTimerRef.current);
      await save();
      const res = await jsonPost('/api/objects', {
        content: contentRef.current,
        object_type: 'snapshot',
        // A snapshot is a frozen copy of the entry, so it inherits the entry's model access
        // rather than defaulting — a sealed entry must not leave a public_ai snapshot behind.
        visibility_targets: [],
        model_access: entry.model_access || 'sealed',
      });
      const snap = await res.json();
      const relId = await ensureRelationType('revises');
      await jsonPost('/api/relation-instances', {
        relation_id: relId,
        source_id: entry.id,
        target_id: snap.id,
        polarity: 1,
      });
      refreshEdges(entry.id);
    } catch (err) {
      console.error('Writer snapshot error:', err);
    } finally {
      setSnapshotting(false);
    }
  };

  const snapshots = edges.filter((e) => e.relation_name === 'revises');
  // tags are rendered as chips in the field above — listing them here too would
  // be the same edge on screen twice, which is the failure the relation tree's
  // guardrail names
  const otherEdges = edges.filter(
    (e) => e.relation_name !== 'revises' && e.relation_name !== 'tagged'
  );

  const activeAccess = describeAccess(access);
  const statusLine = urlMode
    ? 'import — nothing minted'
    : {
        blank: activeAccess,
        unsaved: '…',
        saving: 'Saving…',
        saved: entry ? `Saved · ${entryTierLabel || 'sealed'}` : 'Saved',
        error: 'Save failed — retrying on next pause',
      }[status];

  return (
    <div>
      <Nav />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 20px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <TagField
            tags={tags}
            onAdd={addTag}
            onRemove={removeTag}
            suggest={SUGGESTED_TAGS}
            placeholder={entry ? 'tag it…' : 'tag it — applied when the entry mints'}
          />
          <Explain concept="write-modes" />
        </div>

        {/* The access control: pre-mint only, like the checkbox and then the three-pill row it
            replaced — the status line carries the choice once the entry exists.
            TWO ROWS, not a nine-cell grid: the pill row it replaced put "Private + AI" between
            "Sealed" and "Shared", which read as a severity scale and hid the fact that these
            are independent questions. */}
        {!entry && (
          <div style={{ marginBottom: '14px' }}>
            <AccessControl value={access} onChange={setAccess} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 12px', marginBottom: '14px', fontSize: '13px', color: 'var(--wv-muted)' }}>
          <Explain concept="model-visibility"><span>{statusLine}</span></Explain>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* the doors out: the entry's standing in the graph, and where it
                lands in your own record (entries are asserted_at-dated
                statements — the actor timeline is their natural destination) */}
            {entry && (
              <>
                <Link to={`/object/${entry.id}`} style={{ color: 'var(--wv-accent)' }}>object page →</Link>
                {user?.object_id && (
                  <Link to={`/actor/${user.object_id}`} style={{ color: 'var(--wv-accent)' }}>your timeline →</Link>
                )}
              </>
            )}
            {!urlMode && (
              <button
                onClick={finishAndStartNew}
                disabled={!entry && !content.trim()}
                style={{ cursor: 'pointer', fontSize: '13px' }}
                title="File this one and start a blank entry (Ctrl+Enter)"
              >
                done · new entry
              </button>
            )}
          </span>
        </div>


        {note && <div style={{ color: 'var(--wv-muted)', fontSize: '13px', marginBottom: '10px' }}>{note}</div>}

        {trail.length > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--wv-muted)', marginBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
            <span>filed this sitting:</span>
            {trail.map((t) => (
              <button
                key={t.id}
                onClick={() => openEntry(t.id)}
                style={{ color: 'var(--wv-accent)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px' }}
                title="Reopen this entry"
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Post-done suggestion offer + review (Add's review UI, opt-in) */}
        {suggest && (
          <div style={{ border: '1px solid var(--wv-border)', borderLeft: '3px solid var(--wv-accent)', borderRadius: 'var(--wv-r-xs)', padding: '8px 12px', marginBottom: '12px', fontSize: '13px' }}>
            {suggest.phase === 'offer' && (
              <span style={{ color: 'var(--wv-muted)' }}>
                Filed “{suggest.label}”.{' '}
                <button onClick={fetchSuggestions} style={{ background: 'none', border: 'none', color: 'var(--wv-accent)', cursor: 'pointer', fontSize: '13px', padding: 0 }}>
                  get AI suggestions ▸
                </button>{' '}
                <button onClick={() => setSuggest(null)} style={{ background: 'none', border: 'none', color: 'var(--wv-quiet)', cursor: 'pointer', fontSize: '12px', padding: 0 }}>
                  dismiss
                </button>
              </span>
            )}
            {suggest.phase === 'loading' && <span style={{ color: 'var(--wv-muted)' }}>Fetching suggestions…</span>}
            {suggest.phase === 'review' && (
              <div>
                <p style={{ color: 'var(--wv-muted)', margin: '2px 0 8px' }}>
                  <Explain concept="activation">Toggle off anything you don't want.</Explain>
                </p>
                {suggest.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--wv-sp-2)', marginBottom: '8px' }}>
                    {suggest.tags.map((t, i) => (
                      <span
                        key={i}
                        onClick={() => setSuggest((s) => ({ ...s, tags: s.tags.map((x, j) => (j === i ? { ...x, on: !x.on } : x)) }))}
                        className={t.on ? 'wv-chip on' : 'wv-chip'}
                        style={{ cursor: 'pointer' }}
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
                {suggest.relations.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wv-sp-2)', marginBottom: '8px' }}>
                    {suggest.relations.map((r, i) => {
                      const other = suggest.contextById[r.target_id];
                      return (
                        <div
                          key={i}
                          onClick={() => setSuggest((s) => ({ ...s, relations: s.relations.map((x, j) => (j === i ? { ...x, on: !x.on } : x)) }))}
                          style={{
                            padding: 'var(--wv-sp-2) var(--wv-sp-4)',
                            border: `1px solid ${r.on ? 'var(--wv-border-strong)' : 'var(--wv-border)'}`,
                            borderRadius: 'var(--wv-r-xs)',
                            cursor: 'pointer',
                            opacity: r.on ? 1 : 0.55,
                          }}
                        >
                          <strong>{r.relation_name}</strong> {r.polarity === -1 ? '(−)' : '(+)'} → {other ? other.content : r.target_id}
                          {r.reason && <div style={{ fontSize: 'var(--wv-fs-sm)', color: 'var(--wv-muted)' }}>{r.reason}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {suggest.tags.length === 0 && suggest.relations.length === 0 && (
                  <div style={{ color: 'var(--wv-muted)', marginBottom: '8px' }}>Nothing suggested.</div>
                )}
                <button onClick={applySuggestions} style={{ cursor: 'pointer', fontSize: '13px' }}>Accept selected</button>
                <button onClick={() => setSuggest(null)} style={{ cursor: 'pointer', fontSize: '13px', marginLeft: '10px' }}>Skip</button>
              </div>
            )}
          </div>
        )}

        {/* Import mode: the box holds a lone URL and nothing is minted */}
        {urlMode && (
          <div style={{ border: '1px solid var(--wv-accent)', borderRadius: 'var(--wv-r-sm)', padding: '12px 14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '14px' }}>
              <Explain concept="url-import"><strong>URL detected</strong></Explain>
            </div>
            <div style={{ color: 'var(--wv-muted)', fontSize: '13px', margin: '4px 0 10px' }}>
              Autosave is paused — this imports the page, attributed to its source
              {access.model !== 'sealed' ? ', extracts claims on the way,' : ''} and opens it in the Reader.
            </div>
            <button onClick={importPage} disabled={importBusy} style={{ cursor: 'pointer' }}>
              {importBusy ? 'Importing…' : 'Import page'}
            </button>
          </div>
        )}

        {clipSource && (
          <div style={{ fontSize: '13px', color: 'var(--wv-muted)', marginBottom: '10px' }}>
            <Explain concept="clip-provenance">
              Clipped from <span style={{ wordBreak: 'break-all' }}>{clipSource}</span> — the source page links when the entry mints.
            </Explain>
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Write, or paste anything — a lone URL imports the page instead of saving as text."
          autoFocus
          style={{
            width: '100%',
            minHeight: '58vh',
            border: 'none',
            outline: 'none',
            resize: 'vertical',
            background: 'transparent',
            color: 'var(--wv-text)',
            fontSize: '17px',
            lineHeight: 1.65,
            fontFamily: 'inherit',
            padding: 0,
            boxSizing: 'border-box',
          }}
        />

        {entry && (
          <div style={{ borderTop: '1px solid var(--wv-border)', paddingTop: '12px', marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px' }}>
              <button onClick={() => setShowConnections((s) => !s)} style={{ cursor: 'pointer' }}>
                {showConnections ? '▾' : '▸'} connections ({otherEdges.length})
              </button>
              <button onClick={takeSnapshot} disabled={snapshotting} style={{ cursor: 'pointer' }} title="Freeze a copy of this draft">
                {snapshotting ? 'snapshotting…' : `snapshot (${snapshots.length})`}
              </button>
              <Explain concept="snapshot" />
            </div>

            {showConnections && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {otherEdges.map((e) => (
                  <EdgeSentence
                    key={e.id}
                    edge={e}
                    focusId={entry.id}
                    onVoted={() => refreshEdges(entry.id)}
                    user={user}
                    actions={
                      <button
                        onClick={async () => {
                          await apiFetch(`/api/relation-instances/${e.id}`, { method: 'DELETE' });
                          refreshEdges(entry.id);
                        }}
                        style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--wv-muted)' }}
                        title="Remove this connection"
                      >
                        ✕
                      </button>
                    }
                  />
                ))}
                {snapshots.length > 0 && (
                  <div style={{ fontSize: '13px', color: 'var(--wv-muted)' }}>
                    <Explain concept="snapshot">
                      {snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'}:
                    </Explain>{' '}
                    {snapshots.map((s, i) => (
                      <span key={s.id}>
                        {i > 0 && ' · '}
                        <Link to={`/object/${s.target_id}`} style={{ color: 'inherit' }}>
                          #{snapshots.length - i}
                        </Link>
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <select value={linkRelation} onChange={(e) => setLinkRelation(e.target.value)} style={{ fontSize: '13px' }}>
                    {LINK_RELATIONS.map((r) => (
                      <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <input
                    value={linkQuery}
                    onChange={(e) => setLinkQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchLinkTargets()}
                    placeholder="find or state a goal…"
                    style={{ fontSize: '13px', flex: 1, minWidth: '180px' }}
                  />
                  <button onClick={searchLinkTargets} style={{ cursor: 'pointer', fontSize: '13px' }}>search</button>
                </div>
                {linkResults && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {linkResults.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => addLink(o.id)}
                        className="wv-card"
                        style={{ textAlign: 'left', cursor: 'pointer', fontSize: '13px', padding: '6px 10px' }}
                      >
                        {o.content.length > 110 ? `${o.content.slice(0, 110)}…` : o.content}
                      </button>
                    ))}
                    <button onClick={createAndLink} style={{ cursor: 'pointer', fontSize: '13px', textAlign: 'left', color: 'var(--wv-accent)' }}>
                      + state “{linkQuery.trim()}” as a new goal claim (sealed, private)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* The other ways in — one door (DECISIONS.md 2026-07-07, upheld through
            the 2026-08-07 merge): feed subscriptions and structured creation
            live behind this same surface instead of being doors of their own. */}
        <div style={{ borderTop: '1px solid var(--wv-border)', marginTop: '40px', paddingTop: '10px', fontSize: '13px' }}>
          <button onClick={() => setFeedsOpen((s) => !s)} style={{ cursor: 'pointer', fontSize: '13px' }}>
            {feedsOpen ? '▾' : '▸'} subscribe to feeds
          </button>
          {feedsOpen && (
            <div style={{ margin: '10px 0 6px' }}>
              <p style={{ fontSize: 'var(--wv-fs-md)', color: 'var(--wv-muted)', marginTop: 0 }}>
                <Explain concept="feeds">
                  RSS/Atom items land in the <Link to="/">reading queue</Link> as imported pages.
                </Explain>
              </p>
              <FeedManager />
            </div>
          )}
          <p style={{ color: 'var(--wv-muted)', margin: '10px 0 0' }}>
            Need a tag, a bare link object, or exact control over visibility?{' '}
            <Link to="/create">Create an object directly →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
